import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { firebaseDB } from "../../firebase";
import { UserProfile } from "../../types/budgethelper";
import { useUser } from "../../auth/userContext";

interface BudgetGroupUsersMap {
  [budgetGroupId: string]: UserProfile[];
}

interface BudgetGroupNamesMap {
  [budgetGroupId: string]: string;
}

export const useBudgetGroupUsers = () => {
  const { budgetGroups, loading } = useUser();
  const [budgetGroupUsers, setBudgetGroupUsers] = useState<BudgetGroupUsersMap>({});
  const [budgetGroupNames, setBudgetGroupNames] = useState<BudgetGroupNamesMap>({});

  useEffect(() => {
    if (loading || budgetGroups.length === 0) return;

    const loadUsersForBudgetGroups = async () => {
      const usersMap: BudgetGroupUsersMap = {};
      const namesMap: BudgetGroupNamesMap = {};

      for (const group of budgetGroups) {
        // Store budget group name
        namesMap[group.id] = group.friendlyName;

        // Load users for this budget group
        const users: UserProfile[] = [];
        for (const userId of group.userIds) {
          try {
            const userDoc = await getDoc(doc(firebaseDB, "users", userId));
            if (userDoc.exists()) {
              users.push(userDoc.data() as UserProfile);
            }
          } catch (err) {
            console.error(`Error loading user ${userId}:`, err);
          }
        }
        usersMap[group.id] = users;
      }

      setBudgetGroupUsers(usersMap);
      setBudgetGroupNames(namesMap);
    };

    loadUsersForBudgetGroups();
  }, [budgetGroups, loading]);

  const getUserFriendlyName = (budgetGroupId: string | undefined, userId: string): string | null => {
    if (!budgetGroupId) return null;
    const users = budgetGroupUsers[budgetGroupId];
    if (!users) return null;
    const user = users.find(u => u.userId === userId);
    return user?.friendlyName || null;
  };

  const getBudgetGroupName = (budgetGroupId: string): string | null => {
    return budgetGroupNames[budgetGroupId] || null;
  };

  return { budgetGroupUsers, budgetGroupNames, getUserFriendlyName, getBudgetGroupName };
};
